import { ANALYTICS_SERIES_MAX_EVENTS } from "@loot-os/api-contract";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@loot-os/ui/components/chart";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useRouter, useSearch } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Area, Bar, ComposedChart, XAxis, YAxis } from "recharts";
import { ErrorAlert } from "../components/ErrorAlert";
import { StatCard } from "../components/StatCard";
import { type InsightsSearch, insightSearch, resolveInsight } from "../lib/analyticsInsights";
import { formatRelativeTime } from "../lib/format";
import {
  ANALYTICS_WINDOW_LABELS,
  ANALYTICS_WINDOWS,
  type AnalyticsWindow,
  axisTicks,
  bucketLabel,
  enumerateBuckets,
  granularityOf,
  tickLabel,
  windowRange,
} from "../lib/timeBuckets";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

const chartConfig = {
  // Overview's pairing: the area carries chart-1, the bars beneath it chart-2.
  events: { label: "Events", color: "var(--chart-2)" },
  users: { label: "Shoppers", color: "var(--chart-1)" },
} satisfies ChartConfig;

export function AnalyticsPage() {
  const trpc = useTRPC();
  const router = useRouter();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  // The environment rides the x-loot-environment header from the tenant switcher.
  const search = useSearch({ strict: false }) as { w?: AnalyticsWindow };
  const activeWindow = search.w ?? "7d";
  const granularity = granularityOf(activeWindow);

  const setWindow = (w: AnalyticsWindow) => {
    void router.navigate({
      to: "/tenants/$tenantId/analytics",
      params: { tenantId },
      search: { w },
      replace: true,
    });
  };

  // One "now" per window choice, so the three query inputs agree and do not churn per render.
  const { now, from, to } = useMemo(() => {
    const at = new Date();
    return { now: at, ...windowRange(activeWindow, at) };
  }, [activeWindow]);
  const input = { granularity, from, to };

  // Keep the previous window's data on screen while the next one loads, like Revenue. While a
  // recompute is running (scheduled or on demand) every query polls, so the numbers land on
  // their own when the run records its end.
  const summary = useQuery(
    trpc.analytics.summary.queryOptions(input, {
      placeholderData: (previous) => previous,
      refetchInterval: (query) => (query.state.data?.lastRunStatus === "running" ? 4000 : false),
    }),
  );
  const running = summary.data?.lastRunStatus === "running";
  const timeseries = useQuery(
    trpc.analytics.timeseries.queryOptions(input, {
      placeholderData: (previous) => previous,
      refetchInterval: running ? 4000 : false,
    }),
  );
  const topEvents = useQuery(
    trpc.analytics.topEvents.queryOptions(input, {
      placeholderData: (previous) => previous,
      refetchInterval: running ? 4000 : false,
    }),
  );

  // "Recompute now": the same hourly job the cron runs, started out of schedule. Tenant admins
  // only (the server enforces it too); a stage with no rollup Lambda linked says so.
  const { can } = useTenantRole();
  const queryClient = useQueryClient();
  const recompute = useMutation(
    trpc.analytics.recompute.mutationOptions({
      onSuccess: (outcome) => {
        if (outcome.started) {
          toast.success("Recomputing analytics. The numbers refresh when the run lands.");
        } else if (outcome.reason === "running") {
          toast.info("A recompute is already running.");
        } else {
          toast.info(
            `Analytics were computed a moment ago; try again ${formatRelativeTime(outcome.retryAt, new Date())}.`,
          );
        }
        void queryClient.invalidateQueries({
          queryKey: trpc.analytics.summary.queryOptions(input).queryKey,
        });
      },
    }),
  );
  const recomputeAvailable = summary.data?.recompute.available ?? false;

  // A run that lands changes the summary's "updated at"; the chart and the table read rollups
  // the same run wrote, so they refetch on that signal (a fast local run can finish before the
  // page ever sees `running`, so polling alone would miss it).
  const updatedAt = summary.data?.updatedAt ?? null;
  useEffect(() => {
    if (!updatedAt) return;
    void queryClient.invalidateQueries(trpc.analytics.timeseries.pathFilter());
    void queryClient.invalidateQueries(trpc.analytics.topEvents.pathFilter());
  }, [updatedAt, queryClient, trpc]);

  // Chronological and zero-filled: the rollups hold only non-empty buckets.
  const chartData = useMemo(() => {
    const byBucket = new Map(timeseries.data?.map((row) => [row.bucket, row]) ?? []);
    return enumerateBuckets(granularity, from, to).map((bucket) => {
      const key = bucket.toISOString();
      const row = byBucket.get(key);
      return { bucket: key, events: row?.events ?? 0, users: row?.users ?? 0 };
    });
  }, [timeseries.data, granularity, from, to]);
  const ticks = axisTicks(chartData.map((d) => d.bucket));
  // The events domain is padded so bars top out in the bottom third of the chart, keeping
  // them visually beneath the shoppers area's peaks, like Overview's activity chart.
  const eventsMax = Math.max(1, ...chartData.map((d) => d.events));
  // Floored at 1 so an empty window draws its line on the baseline, not across the middle.
  const usersMax = Math.max(1, ...chartData.map((d) => d.users));
  const perBucket = granularity === "hour" ? "hourly" : "daily";

  // The link that opens these events as an insight over the same window.
  const insightOf = (eventNames: string[]): InsightsSearch =>
    insightSearch(resolveInsight({ w: activeWindow, e: eventNames.join(",") }));
  const topEventNames = (topEvents.data ?? [])
    .slice(0, ANALYTICS_SERIES_MAX_EVENTS)
    .map((row) => row.eventName);

  const error = timeseries.error ?? topEvents.error ?? summary.error;
  const isStale = timeseries.isPlaceholderData || topEvents.isPlaceholderData;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground">
          What shoppers do on this environment's storefront: page views, opens, battles and
          checkouts, counted from the analytics stream. Money stays on Overview and Revenue.
        </p>
      </div>

      {error && <ErrorAlert message={error.message} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={activeWindow} onValueChange={(value) => setWindow(value as AnalyticsWindow)}>
          <TabsList>
            {ANALYTICS_WINDOWS.map((w) => (
              <TabsTrigger key={w} value={w}>
                {ANALYTICS_WINDOW_LABELS[w]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-3">
          <p className="text-xs text-muted-foreground">
            Hourly analytics,{" "}
            {running
              ? "computing now"
              : summary.data?.updatedAt
                ? `updated ${formatRelativeTime(summary.data.updatedAt, now)}`
                : "not yet computed"}
            .
          </p>
          {can("admin") && (
            <Button
              variant="outline"
              size="sm"
              disabled={!recomputeAvailable || running || recompute.isPending}
              title={
                recomputeAvailable
                  ? "Run the hourly rollup now instead of waiting for the next scheduled run"
                  : "Not available on this stage"
              }
              onClick={() => recompute.mutate()}
            >
              <RefreshCw className={running ? "animate-spin" : undefined} />
              {running ? "Computing…" : "Recompute now"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Events" value={summary.data ? formatNumber(summary.data.events) : "—"} />
        <StatCard
          label={`Avg ${perBucket} shoppers`}
          value={summary.data ? formatNumber(summary.data.avgUsersPerBucket) : "—"}
        />
        <StatCard
          label={`Peak ${perBucket} shoppers`}
          value={summary.data ? formatNumber(summary.data.peakUsersPerBucket) : "—"}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Events and shoppers over time</CardTitle>
          <CardDescription>
            {granularity === "hour"
              ? "Per hour, trailing 24 hours, UTC."
              : `Per day, trailing ${ANALYTICS_WINDOW_LABELS[activeWindow]}, UTC.`}{" "}
            Shoppers are distinct per bucket and are not summed across buckets.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-64 w-full">
            <ComposedChart data={chartData} margin={{ left: 0, right: 0, top: 4 }}>
              <defs>
                <linearGradient id="fillUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-users)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--color-users)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="bucket"
                // A point scale runs the area edge to edge; bars would otherwise force a band
                // scale, which leaves half a band empty on each side (wide on a 7-bucket week).
                // The padding is half of maxBarSize, so the two edge bars stay inside the plot.
                scale="point"
                padding={{ left: 8, right: 8 }}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                ticks={ticks}
                // Both edge labels stay: the default drops a first tick that overhangs the plot.
                interval="preserveStartEnd"
                tickFormatter={(value: string) => tickLabel(granularity, new Date(value))}
                className="text-[10px]"
              />
              {/* Events and shoppers differ by an order of magnitude, so each gets its own scale. */}
              <YAxis yAxisId="events" hide domain={[0, eventsMax * 3]} />
              <YAxis yAxisId="users" orientation="right" hide domain={[0, usersMax]} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) =>
                      typeof label === "string" ? bucketLabel(granularity, new Date(label)) : label
                    }
                    formatter={(value, name) => (
                      <>
                        <div
                          className="h-2 w-2 shrink-0 rounded-[2px]"
                          style={{ backgroundColor: `var(--color-${name})` }}
                        />
                        {chartConfig[name as keyof typeof chartConfig]?.label ?? name}
                        <div className="ml-auto pl-4 font-mono font-medium tabular-nums text-foreground">
                          {formatNumber(Number(value))}
                        </div>
                      </>
                    )}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                yAxisId="events"
                dataKey="events"
                fill="var(--color-events)"
                radius={[4, 4, 0, 0]}
                maxBarSize={16}
              />
              <Area
                yAxisId="users"
                dataKey="users"
                type="monotone"
                stroke="var(--color-users)"
                strokeWidth={2}
                fill="url(#fillUsers)"
                dot={false}
              />
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top events</CardTitle>
          <CardDescription>
            Event names by count over the window. Browser events are what shoppers did; server
            events are what happened. An event opens in Insights, charted over time.
          </CardDescription>
          {topEventNames.length > 1 && (
            <CardAction>
              <Link
                to="/tenants/$tenantId/analytics/insights"
                params={{ tenantId }}
                search={insightOf(topEventNames)}
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                Compare in Insights
              </Link>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          <Table aria-busy={isStale} className={isStale ? "opacity-60 transition-opacity" : ""}>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Events</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topEvents.isLoading && (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {topEvents.isSuccess && topEvents.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                    No events in this window yet.
                  </TableCell>
                </TableRow>
              )}
              {topEvents.data?.map((row) => (
                <TableRow key={row.eventName}>
                  <TableCell>
                    <Link
                      to="/tenants/$tenantId/analytics/insights"
                      params={{ tenantId }}
                      search={insightOf([row.eventName])}
                      className="underline-offset-4 hover:underline"
                    >
                      {row.label}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {row.eventName}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatNumber(row.events)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
